// src/pages/Blogs.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import Sidebar from '../components/common/Sidebar';
import Navbar from '../components/forum/Navbar';
import BlogCard from '../components/blog/BlogCard';
import { useBadge } from '../contexts/BadgeContext';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

const BLOGS_PER_PAGE = 12;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache
const SEARCH_DEBOUNCE_MS = 300;

// Page-specific cache for blogs
const pageCache = new Map();

const Blogs = () => {
  const { user, getAuthenticatedApi } = useAuth();
  const { checkForNewBadges } = useBadge();
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [allBlogsCache, setAllBlogsCache] = useState(null);
  const [lastSearchCacheTime, setLastSearchCacheTime] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Refs for cleanup and optimization
  const searchTimeoutRef = useRef(null);
  const componentMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Generate cache key for page-specific caching
  const getPageCacheKey = useCallback((page, sort) => {
    return `page_${page}_sort_${sort}`;
  }, []);

  // Check if cache is valid
  const isCacheValid = useCallback((timestamp) => {
    return Date.now() - timestamp < CACHE_DURATION;
  }, []);

  // Clear all caches
  const clearAllCaches = useCallback(() => {
    pageCache.clear();
    setAllBlogsCache(null);
    setLastSearchCacheTime(0);
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Enhanced fetch with page-specific caching
  const fetchBlogs = useCallback(async (page = 1, sort = 'latest', useCache = true) => {
    if (!componentMountedRef.current) return;

    const cacheKey = getPageCacheKey(page, sort);

    // Check page-specific cache first
    if (useCache && pageCache.has(cacheKey)) {
      const cached = pageCache.get(cacheKey);
      if (isCacheValid(cached.timestamp)) {
        setBlogs(cached.blogs);
        setTotalBlogs(cached.total);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const authenticatedApi = getAuthenticatedApi();
      const skip = (page - 1) * BLOGS_PER_PAGE;
      let endpoint;

      if (sort === 'latest') {
        endpoint = `/blog/latest?limit=${BLOGS_PER_PAGE}&skip=${skip}`;
      } else if (sort === 'most-liked') {
        endpoint = `/blog/most-liked?limit=${BLOGS_PER_PAGE}&skip=${skip}`;
      } else {
        endpoint = `/blog?limit=${BLOGS_PER_PAGE}&skip=${skip}`;
      }

      const res = await authenticatedApi.get(endpoint, {
        signal: abortControllerRef.current.signal
      });

      if (!componentMountedRef.current) return;

      let processedBlogs = [];
      let total = 0;

      if (Array.isArray(res.data)) {
        processedBlogs = [...res.data];

        // Apply client-side sorting if needed
        if (sort === 'latest' && endpoint.includes('/blog?')) {
          processedBlogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } else if (sort === 'most-liked' && endpoint.includes('/blog?')) {
          processedBlogs.sort((a, b) => (b.like_count || 0) - (a.like_count || 0));
        }

        // Smart total calculation
        if (processedBlogs.length < BLOGS_PER_PAGE && page === 1) {
          total = processedBlogs.length;
        } else if (processedBlogs.length < BLOGS_PER_PAGE) {
          total = (page - 1) * BLOGS_PER_PAGE + processedBlogs.length;
        } else {
          total = page * BLOGS_PER_PAGE + 1;
        }
      } else if (res.data.blogs && res.data.total !== undefined) {
        processedBlogs = res.data.blogs;
        total = res.data.total;
      } else {
        processedBlogs = res.data;
        total = res.data.length;
      }

      // Cache the page-specific results
      pageCache.set(cacheKey, {
        blogs: processedBlogs,
        total,
        timestamp: Date.now()
      });

      setBlogs(processedBlogs);
      setTotalBlogs(total);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching blogs:', error);
        if (componentMountedRef.current) {
          setBlogs([]);
          setTotalBlogs(0);
        }
      }
    } finally {
      if (componentMountedRef.current) {
        setIsLoading(false);
      }
      abortControllerRef.current = null;
    }
  }, [getAuthenticatedApi, getPageCacheKey, isCacheValid]);

  // Optimized fetch all blogs for search with caching
  const fetchAllBlogsForSearch = useCallback(async () => {
    // Use cached data if available and valid
    if (allBlogsCache && isCacheValid(lastSearchCacheTime)) {
      return allBlogsCache;
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.get('/blog', {
        signal: abortControllerRef.current.signal
      });

      const blogs = Array.isArray(res.data) ? res.data : [];

      // Cache the results
      setAllBlogsCache(blogs);
      setLastSearchCacheTime(Date.now());

      return blogs;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching all blogs for search:', error);
      }
      return allBlogsCache || [];
    } finally {
      abortControllerRef.current = null;
    }
  }, [getAuthenticatedApi, allBlogsCache, lastSearchCacheTime, isCacheValid]);

  // Optimized search with debouncing and caching
  const performSearch = useCallback(async (query, sort) => {
    if (!query.trim()) {
      setIsSearching(false);
      setCurrentPage(1);
      await fetchBlogs(1, sort);
      return;
    }

    setIsSearching(true);
    setIsLoading(true);

    try {
      const allBlogs = await fetchAllBlogsForSearch();
      const q = query.toLowerCase();

      const filtered = allBlogs.filter((blog) =>
        blog.title?.toLowerCase().includes(q) ||
        blog.author?.name?.toLowerCase().includes(q) ||
        blog.content?.toLowerCase().includes(q) ||
        blog.tags?.some((tag) => tag.name?.toLowerCase().includes(q))
      );

      // Apply sorting
      const sorted = [...filtered].sort((a, b) => {
        if (sort === 'latest') {
          return new Date(b.created_at) - new Date(a.created_at);
        } else if (sort === 'most-liked') {
          return (b.like_count || 0) - (a.like_count || 0);
        }
        return 0;
      });

      if (componentMountedRef.current) {
        setBlogs(sorted);
        setTotalBlogs(sorted.length);
        setCurrentPage(1);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && componentMountedRef.current) {
        console.error('Error during search:', error);
        setBlogs([]);
        setTotalBlogs(0);
      }
    } finally {
      if (componentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchAllBlogsForSearch, fetchBlogs]);

  // Ensure mounted ref is set to true on  mount
  useEffect(() => {
    componentMountedRef.current = true;
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery, sortBy);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, sortBy, performSearch]);

  // Handle pagination and sorting changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      fetchBlogs(currentPage, sortBy);
    }
  }, [currentPage, sortBy, fetchBlogs, searchQuery]);

  // Handle success message and refresh data
  useEffect(() => {
    if (location.state?.success) {
      setShowSuccess(true);

      // Clear all caches to ensure fresh data
      clearAllCaches();

      // Reset to first page and latest sort to show the new blog
      setCurrentPage(1);
      setSortBy('latest');
      setSearchQuery(''); // Clear search to show all blogs

      // Force fetch fresh data
      fetchBlogs(1, 'latest', false);

      // Clear the location state
      window.history.replaceState({}, document.title);

      const timer = setTimeout(() => setShowSuccess(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [location, clearAllCaches, fetchBlogs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Memoized calculations
  const totalPages = useMemo(() => Math.ceil(totalBlogs / BLOGS_PER_PAGE), [totalBlogs]);
  const hasNextPage = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);
  const hasPrevPage = useMemo(() => currentPage > 1, [currentPage]);

  // Optimized blog deletion handler
  const handleBlogDeleted = useCallback((deletedBlogId) => {
    const newTotalBlogs = totalBlogs - 1;
    const newTotalPages = Math.ceil(newTotalBlogs / BLOGS_PER_PAGE);
    const pageToLoad = currentPage > newTotalPages ? Math.max(1, newTotalPages) : currentPage;

    setTotalBlogs(newTotalBlogs);
    setCurrentPage(pageToLoad);

    // Invalidate page-specific cache
    clearAllCaches();

    if (!searchQuery.trim()) {
      fetchBlogs(pageToLoad, sortBy, false); // Force refresh
    } else {
      setBlogs((prevBlogs) => prevBlogs.filter((blog) => blog.id !== deletedBlogId));
    }
  }, [totalBlogs, currentPage, searchQuery, sortBy, fetchBlogs, clearAllCaches]);

  // Optimized handlers
  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    // Clear cache when sorting changes
    clearAllCaches();
  }, [clearAllCaches]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages]);

  const handleCreateBlog = useCallback(() => {
    navigate('/blog/create');
  }, [navigate]);

  // Memoized pagination component
  const PaginationControls = useMemo(() => {
    if (isSearching || totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + maxVisiblePages - 1);

        if (start > 1) {
          pages.push(1);
          if (start > 2) pages.push('...');
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (end < totalPages) {
          if (end < totalPages - 1) pages.push('...');
          pages.push(totalPages);
        }
      }

      return pages;
    };

    return (
      <div className="flex justify-center items-center space-x-2 mt-8 ">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          className={`p-2 rounded-lg border transition-all ${hasPrevPage
            ? 'border-gray-600 text-white hover:bg-gray-700 '
            : 'border-gray-800 text-gray-600 cursor-not-allowed'
            }`}
        >
          <HiChevronLeft className="w-5 h-5" />
        </button>

        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === 'number' && handlePageChange(page)}
            disabled={page === '...'}
            className={`px-3 py-2 rounded-lg border transition-all ${page === currentPage
              ? 'border-blue-500 bg-blue-500 text-white'
              : page === '...'
                ? 'border-transparent text-gray-500 cursor-default'
                : 'border-gray-600 text-white hover:bg-gray-700'
              }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className={`p-2 rounded-lg border transition-all ${hasNextPage
            ? 'border-gray-600 text-white hover:bg-gray-700'
            : 'border-gray-800 text-gray-600 cursor-not-allowed'
            }`}
        >
          <HiChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }, [isSearching, totalPages, currentPage, hasPrevPage, hasNextPage, handlePageChange]);

  return (
    <div className="flex min-h-screen bg-[#0B0C0D]">
      <Sidebar />
      <Navbar />
      <div className="md:ml-16 p-4 pt-20 max-w-screen-xl mx-auto">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-20 right-6 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50"
            >
              Blog published successfully!
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Graphology Blogs</h1>
          
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full lg:w-auto">
                      {['BLOG_WRITER', 'ADMIN', 'MODERATOR'].includes(user?.role?.toUpperCase()) ? (
                        <button
                          onClick={handleCreateBlog}                className="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center transition-colors order-last md:order-first"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Write a Blog
              </button>
            ) : null}

            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              <div className="relative w-full md:w-80">
                <input
                  type="text"
                  placeholder="Search blogs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-800 text-white px-4 py-2 pl-10 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <svg
                  className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-gray-800 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="latest">Latest</option>
                <option value="most-liked">Most Liked</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : blogs.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((blog) => (
                <BlogCard key={blog.id} blog={blog} onBlogDeleted={handleBlogDeleted} />
              ))}
            </div>
            {PaginationControls}
          </>
        ) : (
          <div className="text-center py-16 bg-[#0B0C0D] rounded-lg">
            <svg
              className="w-16 h-16 mx-auto text-gray-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-xl font-medium text-gray-400 mb-2">No blogs found</h3>
            <p className="text-gray-500">
              {searchQuery.trim()
                ? "Try adjusting your search or be the first to write a blog!"
                : "Be the first to write a blog!"
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;